/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { AlertColor } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useRollbar } from '@rollbar/react'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import AccountForm from './account'
import Summary from './summary'
import UserForm from './user'
import { FormRefund as FormRefundApi } from '../../API'
import { createFormRefund } from '../../api/mutations'
import Page from '../../components/elements/Page'
import EmptyState from '../../components/EmptyState'
import PageTitle from '../../components/Page/Title'
import Section from '../../components/Section'
import SnackbarComponent from '../../components/SnackbarComponent'
import { REFUND_PAYMENT_MODES } from '../../constants/models'
import { checkRegisteredUser } from '../../constants/refundUsers'
import { PageLoaderContext } from '../../contexts/PageLoaderContext'
import useEventDetails from '../../hooks/useEventDetails'
import { doMutation } from '../../utils/ApiUtils'

type FormRefund = Omit<Exclude<FormRefundApi, null>, '__typename'>
interface FormData {
  eventId: string
  email: string
  firstName: string
  lastName: string
  paymentMode: string
  bankName: string
  accountName: string
  accountNumber: string
  amountPaid: number
}

const STEP_USER = 0
const STEP_ACCOUNT = 1
const STEP_SUMMARY = 2

const steps = ['Verification', 'Refund to Account', 'Summary']

const SuccessCircleIconContainer = styled('div')(({ theme }) => ({
  display: 'inline-block',
  color: theme.palette.success.main,
  marginBottom: theme.spacing(theme['container'].padding),
  '& svg': {
    fontSize: '40px',
  },
  '& h4': {
    color: theme.palette.success.main,
    marginLeft: '10px',
    display: 'inline-block',
    verticalAlign: 'top',
  },
}))
const FailCircleIconContainer = styled('div')(({ theme }) => ({
  display: 'inline-block',
  color: theme.palette.error.main,
  marginBottom: theme.spacing(theme['container'].padding),
  '& svg': {
    fontSize: '40px',
  },
  '& h4': {
    color: theme.palette.error.main,
    marginLeft: '10px',
    display: 'inline-block',
    verticalAlign: 'top',
  },
}))

const FormRefund = () => {
  const rollbar = useRollbar()
  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(STEP_USER)
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const { setLoading: setPageLoading } = useContext(PageLoaderContext)

  const { slug } = useParams()
  const { event, apiLoading: loading, apiError } = useEventDetails({ slug })

  const [data, setData] = useState<FormData>()
  const [apiSubmitting, setApiSubmitting] = useState(false)

  const [newId, setNewId] = useState(null)
  const [dialogStatus, setDialogStatus] = useState<AlertColor>()

  useEffect(() => {
    setData({
      eventId: '',
      email: '',
      firstName: '',
      lastName: '',
      paymentMode: REFUND_PAYMENT_MODES.GCASH.key,
      bankName: '',
      accountNumber: '',
      accountName: '',
      amountPaid: 0,
    })
  }, [])
  useEffect(() => {
    setPageLoading(loading)
  }, [loading, setPageLoading])

  const displayError = ({
    title,
    message,
  }: {
    title: string
    message: string
  }) => {
    enqueueSnackbar(<SnackbarComponent title={title} message={message} />, {
      variant: 'error',
      anchorOrigin: { horizontal: 'center', vertical: 'top' },
      autoHideDuration: 10000,
      onClose: (event, reason, key) => {
        if (reason === 'instructed') {
          closeSnackbar(key)
        }
      },
    })
  }

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleCancel = () => {
    setData({
      eventId: '',
      email: '',
      firstName: '',
      lastName: '',
      paymentMode: REFUND_PAYMENT_MODES.GCASH.key,
      bankName: '',
      accountNumber: '',
      accountName: '',
      amountPaid: 0,
    })
    handleBack()
  }

  const handleClose = () => {
    navigate('/')
  }

  const errorCallback = (error: any) => {
    rollbar.critical(error)
    if (typeof error === 'object' && 'message' in error)
      displayError(error.message)
    else displayError(error)
  }

  const handleSubmit = useCallback(async () => {
    setApiSubmitting(true)
    setPageLoading(true)
    try {
      const input = {
        ...data,
        eventId: event.id,
      }
      rollbar.info(input)
      const result = (await doMutation('createFormRefund', createFormRefund, {
        input,
      })) as unknown as { data: FormRefund; errors: any }
      if (result.errors) {
        setDialogStatus('error')
      } else {
        setData(null)
        setNewId(result.data.id)
        setDialogStatus('success')
      }
      setApiSubmitting(false)
      setPageLoading(false)
    } catch (e) {
      setApiSubmitting(false)
      setPageLoading(false)
      errorCallback
    }
  }, [data, event])

  const userContent = useCallback(
    () => (
      <UserForm
        user={data}
        editing={!!data}
        setLoading={setPageLoading}
        displayError={displayError}
        updateData={(newData) => {
          const verifiedUser = checkRegisteredUser(slug, newData.email)
          if (verifiedUser) {
            setData({ eventId: event.id, ...newData })
            setPageLoading(false)
            handleNext()
          } else {
            return [
              null,
              {
                title: 'Sorry. Cannot verify email.',
                message:
                  'Please ensure you entered the correct email address used upon registration. Contact support@megatechph.com for assistance.',
              },
            ]
          }
        }}
        onCancel={() => {
          setData(null)
          handleClose()
        }}
      />
    ),
    [data, event],
  )

  const accountContent = useCallback(() => {
    return (
      <AccountForm
        event={event}
        user={data}
        editing={!!data}
        setLoading={setPageLoading}
        displayError={displayError}
        updateData={(newData) => {
          setData(newData)
          handleNext()
        }}
        onBack={() => {
          handleBack()
        }}
        onCancel={() => {
          handleCancel()
        }}
      />
    )
  }, [data, event])

  const summaryContent = useCallback(
    () => (
      <>
        {newId !== null ? (
          <>
            {dialogStatus === 'success' ? (
              <>
                <SuccessCircleIconContainer>
                  <CheckCircleIcon />
                  <Typography variant="h4">Success!</Typography>
                </SuccessCircleIconContainer>
                <Typography variant="body1">
                  We have received your refund details. Your Transaction ID is{' '}
                  {newId}.
                </Typography>

                <Typography variant="caption">
                  <strong>NOTE: </strong>Save this transaction id for your
                  reference.{' '}
                </Typography>
              </>
            ) : (
              <>
                <FailCircleIconContainer>
                  <ErrorIcon />
                  <Typography variant="h4">Failed..</Typography>
                </FailCircleIconContainer>
                <Typography variant="body1">
                  We are unable to capture your data. Please ensure that all
                  required information is provided.
                </Typography>

                <Typography variant="caption">
                  <strong>NOTE: </strong>
                  You may want to contact support@megatechph.com to be safe on
                  your side.
                </Typography>
              </>
            )}

            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
              my={4}>
              <>&nbsp;</>
              <Link href="/events">
                <Button variant="contained" color="secondary">
                  BACK TO EVENTS
                </Button>
              </Link>
            </Box>
          </>
        ) : (
          <Summary
            data={data}
            setLoading={setPageLoading}
            loading={apiSubmitting}
            onSubmit={handleSubmit}
            onCancel={() => {
              handleBack()
            }}
          />
        )}
      </>
    ),
    [data, event],
  )

  const renderContent = () => {
    if (apiError)
      return (
        <EmptyState header={apiError.title} text={apiError.message}>
          <Link href="/events">
            <Button variant="contained" color="secondary">
              BACK TO EVENTS
            </Button>
          </Link>
        </EmptyState>
      )

    if (!event)
      return (
        <EmptyState
          header="Hmm… We cannot find the event you are looking for"
          text=""
        />
      )

    if (!event.refundUntil)
      return (
        <EmptyState header="Hmm… Refund not available for this event" text="" />
      )

    if (dayjs(event.refundUntil).isSameOrBefore(dayjs()))
      return (
        <EmptyState header="Hmm… Refund not available for this event" text="" />
      )

    const stepProps: { completed?: boolean } = {}
    return (
      <Page noPadding>
        <Container maxWidth="sm">
          <PageTitle title={`Refund request for ${event?.name}`} />
          <div style={{ height: 48 }} />
          <Stepper activeStep={activeStep}>
            {steps.map((label) => {
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              )
            })}
          </Stepper>
          <div style={{ height: 32 }} />

          <React.Fragment>
            {activeStep === STEP_USER && userContent()}
            {activeStep === STEP_ACCOUNT && accountContent()}
            {activeStep === STEP_SUMMARY && summaryContent()}
          </React.Fragment>
        </Container>
      </Page>
    )
  }
  return <Section>{!loading && renderContent()}</Section>
}
export default FormRefund
